import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Download, BookOpen, ShoppingBag } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState(sessionId ? 'checking' : 'error');
  const [productName, setProductName] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');
  const [downloadUrlEpub, setDownloadUrlEpub] = useState('');
  const [bundle, setBundle] = useState(null);
  const [productId, setProductId] = useState('');
  const [giftCode, setGiftCode] = useState('');
  const [giftRecipient, setGiftRecipient] = useState('');
  const [giftEmailed, setGiftEmailed] = useState(false);
  const [giftCopied, setGiftCopied] = useState(false);
  const [creditEmail, setCreditEmail] = useState('');
  const [creditStatus, setCreditStatus] = useState('idle');
  const startedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return undefined;
    if (startedRef.current) return undefined;
    startedRef.current = true;

    const maxAttempts = 6;
    let attempt = 0;

    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/checkout/status/${sessionId}`);
        const data = await res.json();
        if (data.payment_status === 'paid') {
          setStatus('success');
          setProductName(data.product_name || '');
          setProductId(data.product_id || '');
          setGiftCode(data.gift_code || '');
          setGiftRecipient(data.gift_recipient || '');
          setGiftEmailed(!!data.gift_emailed);
          if (data.download_url) setDownloadUrl(data.download_url);
          if (data.download_url_epub) setDownloadUrlEpub(data.download_url_epub);
          if (data.downloads) setBundle(data.downloads);
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
        attempt += 1;
        if (attempt >= maxAttempts) {
          setStatus('timeout');
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus('error');
      }
    };

    poll();
  }, [sessionId]);

  const isDigital = !!downloadUrl || !!bundle;

  const claimCredit = async (e) => {
    e.preventDefault();
    const value = creditEmail.trim();
    if (!value) return;
    setCreditStatus('loading');
    try {
      const res = await fetch(`${API}/api/email-capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value, source: 'Deck $15 Credit' }),
      });
      if (!res.ok) throw new Error();
      setCreditStatus('success');
    } catch {
      setCreditStatus('error');
    }
  };

  return (
    <section className="mirror-page" data-testid="checkout-success-page">
      <div className="mirror-intro" style={{ maxWidth: '560px' }}>
        {status === 'checking' && (
          <>
            <h1 className="mirror-title">Processing…</h1>
            <p className="mirror-description">
              Confirming your payment. One moment, sister.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <h1 className="mirror-title" data-testid="checkout-success-title">
              Thank You, Beautiful.
            </h1>
            {productName && (
              <p
                className="mirror-subtitle"
                data-testid="checkout-product-name"
              >
                {productName}
              </p>
            )}

            {giftCode ? (
              <>
                <p className="mirror-description">
                  🎁 Your gift is ready{giftRecipient ? ` for ${giftRecipient}` : ''}!{' '}
                  {giftEmailed
                    ? "We've emailed it straight to their inbox — here's a backup link you can send too."
                    : 'Share the personal link below — they open it and download instantly.'}
                </p>
                <div
                  data-testid="gift-link-box"
                  style={{
                    margin: '1.25rem 0',
                    padding: '1rem',
                    borderRadius: '12px',
                    border: '1px solid rgba(212,175,55,0.4)',
                    background: 'rgba(212,175,55,0.06)',
                    wordBreak: 'break-all',
                    color: '#f8e7b0',
                    fontSize: '0.9rem',
                  }}
                >
                  {`${window.location.origin}/redeem/${giftCode}`}
                </div>
                <div className="mirror-intro-actions" style={{ flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="village-btn village-btn--primary"
                    data-testid="gift-copy-link-btn"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(`${window.location.origin}/redeem/${giftCode}`);
                        setGiftCopied(true);
                      } catch {
                        /* ignore */
                      }
                    }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <Download size={16} strokeWidth={2.2} />
                    {giftCopied ? 'Copied! ✓' : 'Copy Gift Link'}
                  </button>
                  <Link to="/welcome" className="village-btn village-btn--secondary">
                    Enter The Village
                  </Link>
                </div>
                <p style={{ marginTop: '1.25rem', fontSize: '0.78rem', color: 'rgba(245,236,215,0.55)', lineHeight: 1.6 }}>
                  💡 Send the link by text, email, or social — anyone who opens it
                  can download the gift. Save it somewhere safe. 🌙
                </p>
              </>
            ) : isDigital ? (
              bundle ? (
                <>
                  <p className="mirror-description">
                    Your <strong>Trilogy of Truth</strong> is ready — all three
                    books, each in <strong>PDF</strong> (for printing) and{' '}
                    <strong>EPUB</strong> (for phone, tablet, or Kindle).
                  </p>
                  <div style={{ margin: '1.5rem 0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {bundle.map((bk, i) => (
                      <div
                        key={i}
                        data-testid={`bundle-item-${i}`}
                        style={{
                          padding: '1rem 1.25rem',
                          border: '1px solid rgba(212,175,55,0.3)',
                          borderRadius: '12px',
                          background: 'rgba(212,175,55,0.05)',
                          textAlign: 'left',
                        }}
                      >
                        <p style={{ color: '#f8e7b0', fontWeight: 600, marginBottom: '0.6rem' }}>
                          {bk.title}
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <a
                            href={bk.pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            className="village-btn village-btn--primary"
                            data-testid={`bundle-pdf-${i}`}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}
                          >
                            <Download size={15} strokeWidth={2.2} /> PDF
                          </a>
                          <a
                            href={bk.epub}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            className="village-btn village-btn--secondary"
                            data-testid={`bundle-epub-${i}`}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}
                          >
                            <Download size={15} strokeWidth={2.2} /> EPUB
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/welcome"
                    className="village-btn village-btn--secondary"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <BookOpen size={16} strokeWidth={2} />
                    Enter The Village
                  </Link>
                  <p
                    style={{
                      marginTop: '1.25rem',
                      fontSize: '0.78rem',
                      color: 'rgba(245,236,215,0.55)',
                      letterSpacing: '0.04em',
                      lineHeight: 1.6,
                    }}
                  >
                    💡 Tip: download all files now and back them up. New to EPUB?
                    Open it with Apple Books, Google Play Books, or Kindle. 🌙
                  </p>
                </>
              ) : (
              <>
                <p className="mirror-description">
                  {downloadUrlEpub ? (
                    <>
                      Your book is ready in <strong>two formats</strong>.
                      Download both — PDF for printing, EPUB for reading on your
                      phone, tablet, or Kindle.
                    </>
                  ) : (
                    <>
                      Your download is ready. Grab your <strong>PDF</strong> below
                      — keep it forever and print it at home anytime.
                    </>
                  )}
                </p>
                <div className="mirror-intro-actions" style={{ flexWrap: 'wrap' }}>
                  <a
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="village-btn village-btn--primary"
                    data-testid="ebook-download-btn"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <Download size={18} strokeWidth={2.2} />
                    Download PDF
                  </a>
                  {downloadUrlEpub && (
                    <a
                      href={downloadUrlEpub}
                      target="_blank"
                      rel="noopener noreferrer"
                      download="The-Main-Ingredient-FUCK-THATT.epub"
                      className="village-btn village-btn--primary"
                      data-testid="ebook-download-epub-btn"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                      <Download size={18} strokeWidth={2.2} />
                      Download EPUB
                    </a>
                  )}
                  <Link
                    to="/welcome"
                    className="village-btn village-btn--secondary"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <BookOpen size={16} strokeWidth={2} />
                    Enter The Village
                  </Link>
                </div>
                {downloadUrlEpub && (
                  <p
                    style={{
                      marginTop: '1.25rem',
                      fontSize: '0.78rem',
                      color: 'rgba(245,236,215,0.55)',
                      letterSpacing: '0.04em',
                      lineHeight: 1.6,
                    }}
                  >
                    💡 New to EPUB? Open it with Apple Books (iPhone/iPad/Mac),
                    Google Play Books, Kindle, or any free reader app. PDF works
                    everywhere — print pages you want to write on. 🌙
                  </p>
                )}

                {productId === 'digital-card-deck' && (
                  <div
                    data-testid="deck-credit-banner"
                    style={{
                      marginTop: '1.5rem',
                      padding: '1.5rem',
                      borderRadius: '14px',
                      background: 'linear-gradient(155deg, rgba(212,175,55,0.14), rgba(26,18,56,0.5))',
                      border: '1px solid rgba(212,175,55,0.45)',
                      textAlign: 'center',
                    }}
                  >
                    <p style={{ fontSize: '1.6rem', margin: '0 0 0.3rem' }}>🎁</p>
                    <p style={{ color: '#f8e7b0', fontWeight: 700, fontSize: '1.15rem', margin: '0 0 0.4rem' }}>
                      You&apos;ve unlocked <span style={{ color: '#fff' }}>$15 OFF</span> the physical deck!
                    </p>
                    <p style={{ color: 'rgba(245,236,215,0.82)', fontSize: '0.9rem', lineHeight: 1.55, margin: '0 0 1rem' }}>
                      The gold-foiled boxed deck is coming soon. Drop your email and
                      we&apos;ll send your <strong>$15 credit code</strong> the moment it launches.
                    </p>
                    {creditStatus === 'success' ? (
                      <p data-testid="deck-credit-success" style={{ color: '#7CFC9A', fontWeight: 600, fontSize: '0.92rem' }}>
                        ✓ Locked in! Watch your inbox for your $15 code. 🌙
                      </p>
                    ) : (
                      <form
                        onSubmit={claimCredit}
                        style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}
                      >
                        <input
                          type="email"
                          required
                          value={creditEmail}
                          onChange={(e) => setCreditEmail(e.target.value)}
                          placeholder="your@email.com"
                          data-testid="deck-credit-email-input"
                          style={{
                            flex: '1 1 220px',
                            padding: '0.7rem 1rem',
                            borderRadius: '10px',
                            border: '1px solid rgba(212,175,55,0.4)',
                            background: 'rgba(0,0,0,0.3)',
                            color: '#f5ecd7',
                            fontSize: '0.9rem',
                          }}
                        />
                        <button
                          type="submit"
                          disabled={creditStatus === 'loading'}
                          className="village-btn village-btn--primary"
                          data-testid="deck-credit-claim-btn"
                        >
                          {creditStatus === 'loading' ? 'Saving…' : 'Lock in $15'}
                        </button>
                      </form>
                    )}
                    {creditStatus === 'error' && (
                      <p style={{ color: '#ff8a8a', fontSize: '0.82rem', marginTop: '0.5rem' }}>
                        Something went wrong — please try again.
                      </p>
                    )}
                  </div>
                )}
              </>
              )
            ) : (
              <>
                <p className="mirror-description">
                  You&apos;ve just brought a piece of The Alchemy Village into your
                  life. We&apos;ll have your order on its way soon.
                </p>
                <div className="mirror-intro-actions">
                  <Link
                    to="/store"
                    className="village-btn village-btn--primary"
                    data-testid="back-to-store"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <ShoppingBag size={16} strokeWidth={2} />
                    Back to Store
                  </Link>
                  <Link
                    to="/life-map"
                    className="village-btn village-btn--secondary"
                  >
                    Return to Life-Map
                  </Link>
                </div>
              </>
            )}
          </>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <>
            <h1 className="mirror-title">Something Went Sideways</h1>
            <p className="mirror-description">
              {status === 'expired'
                ? 'Your payment session has expired. Please try again.'
                : 'We could not confirm your payment yet. If you were charged, please contact us — we will sort it out.'}
            </p>
            <div className="mirror-intro-actions">
              <Link
                to="/store"
                className="village-btn village-btn--primary"
                data-testid="retry-store"
              >
                Try Again
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
